/**
 * Slug rewriting for portal and www routes, from the `.meta.ts` beside each route. The three hosts,
 * their locale prefixes and the sitemaps: docs/notes/portals/seo-and-slugs.md
 */
// MARK: Portal meta files
import * as portalPrivacyPolicy from '@/routes/$portalId/privacy-policy.meta';
import * as portalLegalDisclaimer from '@/routes/$portalId/legal-disclaimer.meta';

// MARK: WWW meta files
import * as wwwIndex from '@/routes/www/index.meta';
import * as wwwSolutions from '@/routes/www/solutions.meta';
import * as wwwPartnerNgos from '@/routes/www/partner-ngos.meta';
import * as wwwAboutUs from '@/routes/www/about-us.meta';
import * as wwwContact from '@/routes/www/contact.meta';
import * as wwwFaq from '@/routes/www/faq.meta';
import * as wwwLegalDisclaimer from '@/routes/www/legal-disclaimer.meta';
import * as wwwPrivacyPolicy from '@/routes/www/privacy-policy.meta';
import * as wwwCookies from '@/routes/www/cookies.meta';
import * as wwwSelectionCriteria from '@/routes/www/selection-criteria.meta';
import * as wwwLiveCounter from '@/routes/www/live-counter.meta';

// MARK: Archived site meta files (`old.worldcoo.com`)
import * as oldIndex from '@/routes/old/index.meta';
import * as oldAboutUs from '@/routes/old/about-us.meta';
import * as oldContact from '@/routes/old/contact.meta';
import * as oldRoundUp from '@/routes/old/round-up.meta';
import * as oldTransparency from '@/routes/old/transparency.meta';
import * as oldPrivacyPolicy from '@/routes/old/privacy-policy.meta';
import * as oldLegalDisclaimer from '@/routes/old/legal-disclaimer.meta';
import * as oldCookies from '@/routes/old/cookies.meta';
import * as oldSelectionCriteria from '@/routes/old/selection-criteria.meta';

// MARK: Types

/**
 * Recursive because the contact page is two deep (`/contacto/ong/consulta`); a bespoke map per depth is
 * how the rewrite, the language switcher and the canonical start disagreeing about who owns a segment.
 */
export interface SlugNode {
	slugs: Record<string, string>;
	children?: Record<string, SlugNode>;
}

export interface RouteSlugEntry {
	routeName: string;
	slugs: Record<string, string>;
	/** Pages without it are left out of the sitemap. */
	sitemap?: { changefreq: string; priority: string; lastmod: string };
	/**
	 * Only a route that declares its segments is rewritten deeper than one, and only a named segment is
	 * translated: a portal serves `/{portal}/{campaign}/...` under the same rewrite, so a campaign whose
	 * slug matched a legal page's would otherwise be rewritten out from under it.
	 */
	children?: Record<string, SlugNode>;
}

export interface SlugMaps {
	/** External slug (any locale) → internal route segment. */
	slugToRoute: Record<string, string>;
	/** Internal route segment → { locale: slug }. */
	routeToSlugs: Record<string, Record<string, string>>;
	routeChildren: Record<string, Record<string, SlugNode>>;
}

// MARK: Map builder

function buildMaps(routes: RouteSlugEntry[]): SlugMaps {
	const slugToRoute: Record<string, string> = {};
	const routeToSlugs: Record<string, Record<string, string>> = {};
	const routeChildren: Record<string, Record<string, SlugNode>> = {};

	for (const { routeName, slugs, children } of routes) {
		routeToSlugs[routeName] = slugs;
		for (const locale in slugs) {
			if (!Object.hasOwn(slugs, locale)) continue;
			slugToRoute[slugs[locale]!] = routeName;
		}
		if (children) routeChildren[routeName] = children;
	}

	return { slugToRoute, routeToSlugs, routeChildren };
}

function nameOfSlug(nodes: Record<string, SlugNode>, slug: string): string | undefined {
	for (const name in nodes) {
		if (!Object.hasOwn(nodes, name)) continue;
		const slugs = nodes[name]!.slugs;
		for (const locale in slugs) {
			if (Object.hasOwn(slugs, locale) && slugs[locale] === slug) return name;
		}
	}
	return undefined;
}

/**
 * Stops at the first segment the tree does not name, so an invented segment reaches the router
 * untouched and 404s there instead of being rewritten into something that exists.
 */
function walkSegments(
	nodes: Record<string, SlugNode> | undefined,
	segments: string[],
	translate: (node: SlugNode, name: string) => string | undefined,
): string[] {
	const out: string[] = [];
	let level = nodes;
	for (const segment of segments) {
		const name = level ? (level[segment] ? segment : nameOfSlug(level, segment)) : undefined;
		const node = name && level ? level[name] : undefined;
		if (!name || !node) return [...out, ...segments.slice(out.length)];
		out.push(translate(node, name) ?? segment);
		level = node.children;
	}
	return out;
}

export function toInternalSegments(routeName: string, segments: string[], context: SlugContext): string[] {
	return walkSegments(SLUG_CONTEXTS[context].routeChildren[routeName], segments, (_node, name) => name);
}

export function toLocaleSegments(
	routeName: string,
	segments: string[],
	locale: string,
	context: SlugContext,
): string[] {
	return walkSegments(SLUG_CONTEXTS[context].routeChildren[routeName], segments, (node) => node.slugs[locale]);
}

export function hasChildSegments(routeName: string, context: SlugContext): boolean {
	return SLUG_CONTEXTS[context].routeChildren[routeName] !== undefined;
}

// MARK: Portal routes

export const portalRoutes: RouteSlugEntry[] = [portalPrivacyPolicy, portalLegalDisclaimer];
export const portalSlugs = buildMaps(portalRoutes);

// MARK: Archived site routes (`old.worldcoo.com`)

// Reachable but kept out of the index: it shares almost every slug with www, so an indexed copy is duplicate
// content. See `old/robots[.]txt.tsx` and `old/-components/page-seo.tsx`. Nothing new belongs here.
// MARK: -

export const oldRoutes: RouteSlugEntry[] = [
	oldIndex,
	oldAboutUs,
	oldContact,
	oldRoundUp,
	oldTransparency,
	oldPrivacyPolicy,
	oldLegalDisclaimer,
	oldCookies,
	oldSelectionCriteria,
];

export const oldSlugs = buildMaps(oldRoutes);

// MARK: WWW routes

export const wwwRoutes: RouteSlugEntry[] = [
	wwwIndex,
	wwwSolutions,
	wwwPartnerNgos,
	wwwAboutUs,
	wwwContact,
	wwwFaq,
	wwwLegalDisclaimer,
	wwwPrivacyPolicy,
	wwwCookies,
	wwwSelectionCriteria,
	wwwLiveCounter,
];
export const wwwSlugs = buildMaps(wwwRoutes);

// MARK: Contexts

/**
 * Decided by the HOST: the router's rewrite never consults another context's map, so every consumer
 * has to name the same one or it answers with a slug the host does not serve.
 */
export const SLUG_CONTEXTS = {
	www: wwwSlugs,
	old: oldSlugs,
	portal: portalSlugs,
} as const;

export type SlugContext = keyof typeof SLUG_CONTEXTS;

// MARK: Language switcher helper

const slugToSlugsByContext = Object.fromEntries(
	Object.entries(SLUG_CONTEXTS).map(([context, maps]) => {
		const bySlug: Record<string, Record<string, string>> = {};
		for (const route in maps.routeToSlugs) {
			if (!Object.hasOwn(maps.routeToSlugs, route)) continue;
			const slugsByLocale = maps.routeToSlugs[route]!;
			for (const locale in slugsByLocale) {
				if (!Object.hasOwn(slugsByLocale, locale)) continue;
				bySlug[slugsByLocale[locale]!] = slugsByLocale;
			}
		}
		return [context, bySlug];
	}),
) as Record<SlugContext, Record<string, Record<string, string>>>;

/** The language switcher's entry point: segments arrive in whatever locale the current URL uses. */
export function translateChildSegments(
	slug: string,
	segments: string[],
	targetLocale: string,
	context: SlugContext,
): string[] {
	const routeName = SLUG_CONTEXTS[context].slugToRoute[slug];
	if (!routeName) return segments;
	return toLocaleSegments(routeName, toInternalSegments(routeName, segments, context), targetLocale, context);
}

/**
 * Within one context: `about-us` is both www's and the archived site's `en` slug for different pages,
 * so one merged map would answer with a path only the other host serves.
 */
export function translateSlug(slug: string, targetLocale: string, context: SlugContext): string | undefined {
	return slugToSlugsByContext[context][slug]?.[targetLocale];
}
